/**
 * TusCourses PWA - PrimeVue Theme Preset
 *
 * Custom preset based on Aura theme with TuS Wannsee brand colors.
 *
 * Key design decisions:
 * - Primary buttons use TuS Yellow (#fcea10) with black text for contrast
 * - Dark mode uses slightly adjusted yellow tones for better visibility
 * - Surface colors based on neutral grays for readability
 */

import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import { primary, secondary, success, warning, error } from './colors'

export const TusWannseePreset = definePreset(Aura, {
  semantic: {
    // Primary Color Palette - TuS Yellow
    primary: {
      50: primary[50],
      100: primary[100],
      200: primary[200],
      300: primary[300],
      400: primary[400],
      500: primary[500],
      600: primary[600],
      700: primary[700],
      800: primary[800],
      900: primary[900],
      950: primary[950],
    },

    // Color Scheme Overrides
    colorScheme: {
      light: {
        primary: {
          color: primary[500],
          // Use black text on yellow for AAA contrast
          // (Themes 3: fruehere `inverseColor`, siehe {primary.contrast.color})
          contrastColor: secondary[900],
          hoverColor: primary[600],
          activeColor: primary[700],
        },
        highlight: {
          background: primary[100],
          focusBackground: primary[200],
          color: primary[900],
          focusColor: primary[950],
        },
        surface: {
          0: '#ffffff',
          50: secondary[50],
          100: secondary[100],
          200: secondary[200],
          300: secondary[300],
          400: secondary[400],
          500: secondary[500],
          600: secondary[600],
          700: secondary[700],
          800: secondary[800],
          900: secondary[900],
          950: secondary[950],
        },
      },
      dark: {
        primary: {
          // Slightly brighter yellow in dark mode
          color: primary[400],
          contrastColor: secondary[950],
          hoverColor: primary[300],
          activeColor: primary[200],
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
          focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
        surface: {
          0: secondary[950],
          50: secondary[900],
          100: secondary[800],
          200: secondary[700],
          300: secondary[600],
          400: secondary[500],
          500: secondary[400],
          600: secondary[300],
          700: secondary[200],
          800: secondary[100],
          900: secondary[50],
          950: '#ffffff',
        },
      },
    },
  },

  // Component-specific overrides
  components: {
    button: {
      root: {
        borderRadius: '{border.radius.lg}',
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: primary[500],
              hoverBackground: primary[600],
              activeBackground: primary[700],
              // Black text on yellow for AAA contrast
              color: secondary[900],
            },
            secondary: {
              background: '{surface.100}',
              hoverBackground: '{surface.200}',
              activeBackground: '{surface.300}',
              color: '{surface.700}',
            },
            success: {
              background: success[500],
              hoverBackground: success[600],
              activeBackground: success[700],
              color: '#ffffff',
            },
            warn: {
              background: warning[500],
              hoverBackground: warning[600],
              activeBackground: warning[700],
              color: '#ffffff',
            },
            danger: {
              background: error[500],
              hoverBackground: error[600],
              activeBackground: error[700],
              color: '#ffffff',
            },
          },
        },
        dark: {
          root: {
            primary: {
              background: primary[400],
              hoverBackground: primary[300],
              activeBackground: primary[200],
              color: secondary[950],
            },
          },
        },
      },
    },

    card: {
      root: {
        borderRadius: '{border.radius.xl}',
        shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      },
      body: {
        padding: '1rem',
        gap: '0',
      },
      title: {
        fontWeight: '600',
      },
    },

    inputtext: {
      root: {
        borderRadius: '{border.radius.lg}',
      },
    },

    toast: {
      root: {
        borderRadius: '{border.radius.lg}',
      },
    },

    dialog: {
      root: {
        borderRadius: '{border.radius.xl}',
      },
    },

    badge: {
      root: {
        borderRadius: '{border.radius.md}',
      },
    },

    chip: {
      root: {
        borderRadius: '{border.radius.lg}',
      },
    },
  },
})
